<template>
  <div class="lia-doc-page">
    <div class="legal-strip no-print">
      <CygnusIcon name="shield" />
      <span><strong>This is documentation, not legal advice.</strong> Yorlin structures and stores the decisions you make so you have defensible paperwork on file — it doesn't tell you whether your outreach is lawful.</span>
    </div>

    <div v-if="loading" class="lia-loading"><CygnusIcon name="spinner" :spin="true" /> Loading…</div>
    <div v-else-if="error" class="alert alert-warning">{{ error }}</div>

    <div v-else-if="doc" class="doc-wrap">
      <div class="doc">
        <div class="doc__head">
          <div>
            <h3 class="doc__title">Legitimate Interests Assessment — {{ doc.title || 'Untitled' }}</h3>
            <div class="doc__sub">
              <span><span class="k">Version</span> v{{ doc.version }}<span v-if="doc.status === 'current'"> (current)</span><span v-else-if="doc.status === 'superseded'"> (superseded)</span><span v-else> (draft)</span></span>
              <span><span class="k">Generated</span> {{ fmtDate(doc.created) }}</span>
              <span v-if="doc.audience_label"><span class="k">Covers audience</span> {{ doc.audience_label }}</span>
            </div>
          </div>
          <div class="doc__actions no-print">
            <span v-if="doc.review_due_at" class="review-chip" :class="{ ok: !reviewSoon, warn: reviewSoon }">
              <CygnusIcon name="clock" /> Review due {{ fmtDate(doc.review_due_at) }}
            </span>
            <button v-if="doc.status !== 'draft'" class="btn btn-primary" :disabled="!!downloadingV" @click="downloadPdf(doc)"><CygnusIcon name="file" class="me-1" />{{ downloadingV === doc.version ? 'Preparing…' : 'Download PDF' }}</button>
            <button v-if="doc.status !== 'draft'" class="btn btn-outline" @click="print"><CygnusIcon name="file" class="me-1" />Print view</button>
            <a v-if="doc.status === 'draft'" class="btn btn-primary" :href="`/admin/compliance/lia/${doc.id}/edit`"><CygnusIcon name="pen" class="me-1" />Continue draft</a>
          </div>
        </div>

        <div v-if="doc.status === 'draft'" class="draft-note no-print">
          This assessment is still a <strong>draft</strong> — it hasn't been generated yet, so it isn't counted as "on file". Finish and generate it to store an immutable, dated record.
        </div>

        <div class="doc__body">
          <div class="lia-sec">
            <div class="lia-sec__h"><span class="lia-sec__n">1</span><span class="lia-sec__t">Purpose test</span><span class="lia-sec__tag">Your words</span></div>
            <div class="qa"><div class="qa__q">Audience</div><div class="qa__a tenant">{{ a('audience_description') }}</div></div>
            <div class="qa"><div class="qa__q">Legitimate interest identified</div><div class="qa__a tenant">{{ a('purpose') }}</div></div>
            <div class="qa"><div class="qa__q">Reasonable expectations</div><div class="qa__a tenant">{{ a('reasonable_expectations') }}</div></div>
          </div>

          <div class="lia-sec">
            <div class="lia-sec__h"><span class="lia-sec__n">2</span><span class="lia-sec__t">Necessity test</span><span class="lia-sec__tag">Your words</span></div>
            <div class="qa"><div class="qa__q">Why processing is necessary for the purpose</div><div class="qa__a tenant">{{ a('necessity') }}</div></div>
          </div>

          <div class="lia-sec">
            <div class="lia-sec__h"><span class="lia-sec__n">3</span><span class="lia-sec__t">Balancing test</span><span class="lia-sec__tag">Your words</span></div>
            <div class="qa"><div class="qa__q">Impact on the individual &amp; how it is kept proportionate</div><div class="qa__a tenant">{{ a('balancing') }}</div></div>
            <div v-if="a('exclusions')" class="qa"><div class="qa__q">Recipients excluded from legitimate interest</div><div class="qa__a tenant">{{ a('exclusions') }}</div></div>
          </div>

          <div class="lia-sec">
            <div class="lia-sec__h"><span class="lia-sec__n">4</span><span class="lia-sec__t">Safeguards in place</span><span class="lia-sec__tag">Platform-enforced</span></div>
            <div class="safeguards">
              <div v-for="f in safeguardFacts" :key="f.key" class="sg">
                <CygnusIcon name="check" /><span><strong>{{ f.title }}.</strong> {{ f.detail }}</span><span class="auto">{{ f.tag }}</span>
              </div>
            </div>
            <div v-if="a('tenant_safeguards')" class="qa" style="margin-top:12px"><div class="qa__q">Your own additional safeguards</div><div class="qa__a tenant">{{ a('tenant_safeguards') }}</div></div>
          </div>

          <div class="lia-sec">
            <div class="lia-sec__h"><span class="lia-sec__n">5</span><span class="lia-sec__t">Review date</span></div>
            <div class="qa"><div class="qa__a">This assessment should be reviewed at least annually, or sooner if your audience, purpose or method changes.<span v-if="doc.review_due_at"> <strong>Next review: {{ fmtDate(doc.review_due_at) }}.</strong> Yorlin will nudge you 30 days before.</span><span v-if="a('review_notes')"> {{ a('review_notes') }}</span></div></div>
          </div>

          <div class="doc-footer print-only">
            Documentation, not legal advice. Generated by Yorlin on {{ fmtDate(doc.created) }} · Version {{ doc.version }}. This record documents the tenant's own decisions and the safeguards the platform enforces; it does not constitute legal advice or certify lawfulness.
          </div>
        </div>
      </div>

      <aside class="no-print">
        <div class="vhist">
          <div class="vhist__h">Version history</div>
          <div class="vtl">
            <div v-for="v in doc.versions" :key="v.id" class="vrow" :class="{ now: v.id === doc.id }">
              <div class="vrow__v">
                <a :href="`/admin/compliance/lia/${v.id}`">v{{ v.version }}</a>
                <span v-if="v.status === 'current'" class="cur">Current</span>
                <span v-else-if="v.status === 'draft'" class="cur draft">Draft</span>
                <a v-if="v.status !== 'draft'" class="vdl" href="#" :title="`Download v${v.version} PDF`" @click.prevent="downloadPdf(v)">
                  <CygnusIcon :name="downloadingV === v.version ? 'spinner' : 'file'" :spin="downloadingV === v.version" />
                </a>
              </div>
              <div class="vrow__d">{{ fmtDate(v.created) }}</div>
            </div>
          </div>
          <hr class="soft">
          <div class="vhist__note">Each version is an <strong>immutable snapshot</strong> stamped with author + timestamp. Older versions stay printable — that's the point: you can show the assessment as it stood when a given campaign ran.</div>
          <button v-if="doc.status === 'current'" class="btn btn-secondary btn-sm mt-2" :disabled="busy" @click="newRevision">
            <CygnusIcon name="pen" class="me-1" />Start a new revision
          </button>
        </div>
        <div class="disclaimer">
          <CygnusIcon name="shield" />
          <span><strong>Yorlin keeps good records — it isn't your lawyer.</strong> For a definitive view on your specific situation, get a professional review.</span>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: 'AdminLiaDocument',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return { api: useApi(), loading: true, error: null, doc: null, facts: [], busy: false, downloadingV: null };
  },
  computed: {
    docId() { const m = window.location.pathname.match(/\/admin\/compliance\/lia\/(\d+)/); return m ? parseInt(m[1], 10) : null; },
    reviewSoon() {
      if (!this.doc?.review_due_at) return false;
      return (this.doc.review_due_at - Date.now() / 1000) < 30 * 86400;
    },
    safeguardFacts() {
      // Prefer the frozen snapshot on the record (what was enforced when it was
      // generated); fall back to the live facts for a draft that has none yet.
      const snap = this.doc?.safeguards;
      if (snap && Array.isArray(snap.facts)) return snap.facts;
      return this.facts;
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true; this.error = null;
      try {
        const res = await this.api.get(`/api/v1/admin/compliance/lia/${this.docId}`);
        const body = res?.data ?? res;
        this.doc = body?.assessment;
        this.facts = Array.isArray(body?.safeguards_facts) ? body.safeguards_facts : [];
      } catch (e) {
        this.error = this.errText(e, 'Could not load this assessment.');
      }
      this.loading = false;
    },
    a(key) {
      const v = this.doc?.answers?.[key];
      return (v === undefined || v === null || v === '') ? '—' : v;
    },
    fmtDate(ts) { return ts ? new Date(ts * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'; },
    print() { window.print(); },
    /**
     * Download a version's real PDF (server-rendered, per-version). The endpoint
     * is Ops::COMPLIANCE + plan-gated, so useApi()'s axios instance carries the
     * Bearer token — a plain <a href> can't, so we fetch a blob and trigger the
     * download via an object URL (same pattern as PipelinesHome.exportMatches).
     * Any version in the history downloads (a superseded version is the point).
     */
    async downloadPdf(v) {
      if (this.downloadingV) return;
      const version = v?.version;
      const group = this.doc?.group_key;
      if (!group || !version) return;
      this.downloadingV = version;
      try {
        const res = await this.api.get(`/api/v1/admin/compliance/lia/${group}/versions/${version}/pdf`, { responseType: 'blob' });
        const blob = res instanceof Blob ? res : (res?.data instanceof Blob ? res.data : res);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `LIA-${group}-v${version}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.error = this.errText(e, 'Could not download the PDF.');
      } finally {
        this.downloadingV = null;
      }
    },
    async newRevision() {
      this.busy = true;
      try {
        const res = await this.api.post(`/api/v1/admin/compliance/lia/${this.doc.id}/revision`, {});
        const a = (res?.data ?? res)?.assessment;
        window.location.href = `/admin/compliance/lia/${a?.id}/edit`;
      } catch (e) { this.error = this.errText(e, 'Could not start a revision.'); this.busy = false; }
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      if (body?.error === 'plan_gated') return body.message || fallback;
      return body?.message || body?.error || fallback;
    },
  },
};
</script>

<style scoped>
.lia-doc-page { padding:24px; max-width:1180px; }
.legal-strip { display:flex; align-items:center; gap:11px; padding:10px 18px; background:linear-gradient(180deg,#FBFAFF,#F7F5FF); border:1px solid #E0D2FB; border-radius:10px; font-size:.82rem; color:#4B3A73; line-height:1.4; margin-bottom:18px; }
.legal-strip i { color:#7C3AED; }
.lia-loading { padding:40px; color:#697086; }
.doc-wrap { display:grid; grid-template-columns:1fr 260px; gap:20px; }
@media(max-width:1000px){ .doc-wrap{ grid-template-columns:1fr; } }
.doc { background:#fff; border:1px solid #EAECF3; border-radius:14px; overflow:hidden; }
.doc__head { padding:20px 26px; border-bottom:1px solid #EAECF3; background:linear-gradient(180deg,#FBFCFF,#fff); display:flex; align-items:flex-start; gap:14px; flex-wrap:wrap; }
.doc__title { font-size:1.15rem; font-weight:700; margin:0; }
.doc__sub { font-size:.8rem; color:#697086; margin-top:3px; display:flex; gap:12px; flex-wrap:wrap; }
.doc__sub .k { color:#9AA0AC; }
.doc__actions { margin-left:auto; display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
.draft-note { margin:16px 26px 0; padding:11px 14px; background:#FFF4E6; border:1px solid #F0DCBB; border-radius:9px; font-size:.82rem; color:#9A5B12; }
.doc__body { padding:8px 26px 24px; }
.lia-sec { padding:20px 0; border-bottom:1px solid #EAECF3; }
.lia-sec:last-child { border-bottom:none; }
.lia-sec__h { display:flex; align-items:center; gap:10px; margin-bottom:12px; }
.lia-sec__n { width:26px; height:26px; border-radius:8px; background:#EEF0FF; color:#4338CA; display:flex; align-items:center; justify-content:center; font-size:.74rem; font-weight:800; flex:none; }
.lia-sec__t { font-size:.96rem; font-weight:700; }
.lia-sec__tag { margin-left:auto; font-size:.62rem; font-weight:800; text-transform:uppercase; letter-spacing:.04em; color:#0F766E; background:#E4F7F4; border:1px solid #B6EBE4; border-radius:999px; padding:2px 9px; }
.qa { margin-bottom:13px; }
.qa__q { font-size:.78rem; font-weight:600; color:#697086; margin-bottom:3px; }
.qa__a { font-size:.9rem; line-height:1.55; color:#1A1C23; white-space:pre-wrap; }
.qa__a.tenant { padding-left:11px; border-left:3px solid #EEF0FF; }
.safeguards { display:grid; gap:8px; }
.sg { display:flex; gap:10px; align-items:flex-start; padding:10px 12px; background:#E4F7F4; border:1px solid #B6EBE4; border-radius:9px; font-size:.84rem; line-height:1.45; color:#0B5D57; }
.sg i { color:#14B8A6; flex:none; margin-top:1px; }
.sg .auto { margin-left:auto; font-size:.56rem; font-weight:800; text-transform:uppercase; color:#0F766E; background:#fff; border:1px solid #B6EBE4; border-radius:999px; padding:1px 6px; flex:none; white-space:nowrap; height:fit-content; }
.review-chip { display:inline-flex; align-items:center; gap:7px; font-size:.74rem; font-weight:700; padding:5px 11px; border-radius:999px; }
.review-chip.ok { background:#E4F7F4; color:#0F766E; border:1px solid #B6EBE4; }
.review-chip.warn { background:#FFF4E6; color:#9A5B12; border:1px solid #F0DCBB; }
.vhist { background:#fff; border:1px solid #EAECF3; border-radius:14px; padding:16px; align-self:start; }
.vhist__h { font-size:.68rem; font-weight:800; text-transform:uppercase; letter-spacing:.06em; color:#9AA0AC; margin-bottom:14px; }
.vrow { padding:0 0 12px; }
.vrow__v { font-size:.82rem; font-weight:700; }
.vrow__v a { color:#4338CA; text-decoration:none; }
.vrow.now .vrow__v a { color:#1A1C23; }
.vrow__v .cur { font-size:.56rem; font-weight:800; text-transform:uppercase; color:#4338CA; background:#EEF0FF; border-radius:4px; padding:0 5px; margin-left:6px; }
.vrow__v .cur.draft { color:#9A5B12; background:#FFF4E6; }
.vrow__v .vdl { float:right; color:#9AA0AC; text-decoration:none; font-size:.82rem; }
.vrow__v .vdl:hover { color:#4338CA; }
.vrow__d { font-size:.7rem; color:#9AA0AC; margin-top:2px; }
.soft { border:none; border-top:1px solid #EAECF3; margin:6px 0 12px; }
.vhist__note { font-size:.74rem; color:#9AA0AC; line-height:1.5; }
.disclaimer { margin-top:16px; padding:14px 16px; background:linear-gradient(180deg,#FBFAFF,#F8F6FF); border:1px solid #E0D2FB; border-radius:12px; display:flex; gap:11px; align-items:flex-start; font-size:.78rem; line-height:1.55; color:#4B3A73; }
.disclaimer i { color:#7C3AED; flex:none; margin-top:2px; }
.btn { font-weight:500; font-size:.82rem; padding:.4rem .85rem; border-radius:6px; border:1px solid transparent; cursor:pointer; display:inline-flex; align-items:center; text-decoration:none; }
.btn-primary { background:#4F46E5; border-color:#4F46E5; color:#fff; }
.btn-outline { color:#4338CA; border-color:#4F46E5; background:#fff; }
.btn-outline:hover { background:#4F46E5; color:#fff; }
.btn-secondary { color:#697086; border-color:#EAECF3; background:#fff; }
.btn-sm { font-size:.76rem; padding:.3rem .6rem; }
.doc-footer { font-size:.72rem; color:#9AA0AC; padding-top:16px; line-height:1.5; }
.print-only { display:none; }
@media print {
  .no-print { display:none !important; }
  .print-only { display:block; }
  .lia-doc-page { padding:0; max-width:none; }
  .doc { border:none; }
  .doc-wrap { grid-template-columns:1fr; }
}
</style>
